import React, { useState } from 'react'
import { Lock, Plus, Pencil, Trash2, Loader2, Tag, Target, ChevronDown, ChevronUp } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../components/ui/PageHeader'
import { AlertBanner } from '../components/ui/AlertBanner'
import { useTenant } from '../hooks/useTenant'
import {
  useBusinessUnits,
  useCreateBusinessUnit,
  useUpdateBusinessUnit,
  useDeleteBusinessUnit,
  useBusinessUnitMapping,
  useUpdateBusinessUnitMapping,
  useBusinessUnitTargets,
  useUpsertBusinessUnitTargets,
  BusinessUnit,
} from '../hooks/useBusinessUnits'
import { PlanRequiredError } from '../lib/api'
import { formatKpiEur } from '../lib/formatters'

// ---------------------------------------------------------------------------
// Paywall banner
// ---------------------------------------------------------------------------

function EnterprisePaywall() {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-6 text-center">
      <div className="rounded-full bg-indigo-500/10 p-5">
        <Lock className="w-10 h-10 text-indigo-400" />
      </div>
      <div>
        <h2 className="text-xl font-semibold text-[--km-text-primary] mb-2">
          Funzionalità Enterprise
        </h2>
        <p className="text-[--km-text-secondary] max-w-md">
          Le Business Unit sono disponibili nel piano Enterprise.
          Segmenta i KPI per divisione aziendale e imposta target per area.
        </p>
      </div>
      <Link
        to="/upgrade"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white font-medium transition-colors"
      >
        Scopri il piano Enterprise
      </Link>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Form creazione / modifica BU
// ---------------------------------------------------------------------------

interface BuFormProps {
  initial?: BusinessUnit
  onSave: (data: { name: string; code: string; description: string; color: string }) => void
  onCancel: () => void
  isLoading: boolean
}

function BuForm({ initial, onSave, onCancel, isLoading }: BuFormProps) {
  const [name, setName] = useState(initial?.name ?? '')
  const [code, setCode] = useState(initial?.code ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [color, setColor] = useState(initial?.color ?? '#6366f1')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !code.trim()) return
    onSave({ name: name.trim(), code: code.trim(), description, color })
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-[--km-text-secondary] uppercase tracking-wide">
            Nome <span className="text-red-400">*</span>
          </label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="es. Tecnologia"
            required
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:border-indigo-500"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-[--km-text-secondary] uppercase tracking-wide">
            Codice <span className="text-red-400">*</span>
          </label>
          <input
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder="es. TECH"
            required
            maxLength={50}
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:border-indigo-500 font-mono"
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-[--km-text-secondary] uppercase tracking-wide">
          Descrizione
        </label>
        <input
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Descrizione opzionale"
          className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:border-indigo-500"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-[--km-text-secondary] uppercase tracking-wide">
          Colore
        </label>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={color}
            onChange={e => setColor(e.target.value)}
            className="w-10 h-10 rounded-lg border border-white/10 bg-transparent cursor-pointer"
          />
          <span className="text-sm text-[--km-text-secondary] font-mono">{color}</span>
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={isLoading || !name.trim() || !code.trim()}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white text-sm font-medium transition-colors"
        >
          {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
          {initial ? 'Salva modifiche' : 'Crea Business Unit'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg border border-white/10 text-[--km-text-secondary] text-sm hover:text-[--km-text-primary] hover:bg-white/5 transition-colors"
        >
          Annulla
        </button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Pannello mapping service_line
// ---------------------------------------------------------------------------

function MappingPanel({ bu, tenantServiceLines }: { bu: BusinessUnit; tenantServiceLines: string[] }) {
  const { data: mapping, isLoading } = useBusinessUnitMapping(bu.id)
  const updateMapping = useUpdateBusinessUnitMapping()
  const [selected, setSelected] = useState<string[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const current = selected ?? mapping?.service_lines ?? []

  const toggle = (sl: string) => {
    setSelected(prev => {
      const base = prev ?? mapping?.service_lines ?? []
      return base.includes(sl) ? base.filter(s => s !== sl) : [...base, sl]
    })
  }

  const handleSave = async () => {
    setError(null)
    try {
      await updateMapping.mutateAsync({ buId: bu.id, serviceLines: current })
      setSelected(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Errore salvataggio mapping')
    }
  }

  if (isLoading) return <p className="text-xs text-[--km-text-muted]">Caricamento…</p>

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-[--km-text-secondary]">
        Assegna le service line a questa BU. Ogni service line può appartenere a una sola BU.
      </p>
      <div className="flex flex-wrap gap-2">
        {tenantServiceLines.map(sl => (
          <button
            key={sl}
            onClick={() => toggle(sl)}
            className={`px-3 py-1 rounded-full text-xs font-mono font-medium border transition-colors ${
              current.includes(sl)
                ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300'
                : 'bg-white/5 border-white/10 text-[--km-text-secondary] hover:border-white/20'
            }`}
          >
            {sl}
          </button>
        ))}
      </div>
      {selected !== null && (
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={updateMapping.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white text-xs font-medium transition-colors"
          >
            {updateMapping.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
            Salva
          </button>
          <button
            onClick={() => setSelected(null)}
            className="text-xs text-[--km-text-muted] hover:text-[--km-text-secondary]"
          >
            Annulla
          </button>
        </div>
      )}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pannello target annuali
// ---------------------------------------------------------------------------

function TargetsPanel({ bu }: { bu: BusinessUnit }) {
  const { data: targets, isLoading } = useBusinessUnitTargets(bu.id)
  const upsert = useUpsertBusinessUnitTargets()
  const currentYear = new Date().getFullYear()
  const [editYear, setEditYear] = useState<number | null>(null)
  const [rev, setRev] = useState('')
  const [mar, setMar] = useState('')
  const [util, setUtil] = useState('')
  const [error, setError] = useState<string | null>(null)

  const openEdit = (year: number) => {
    const existing = targets?.find(t => t.year === year)
    setEditYear(year)
    setRev(existing?.target_revenue ?? '')
    setMar(existing?.target_margin ?? '')
    setUtil(existing?.target_utilization ?? '')
    setError(null)
  }

  const handleSave = async () => {
    if (!editYear) return
    setError(null)
    try {
      await upsert.mutateAsync({
        buId: bu.id,
        year: editYear,
        payload: {
          target_revenue: rev || undefined,
          target_margin: mar || undefined,
          target_utilization: util || undefined,
        },
      })
      setEditYear(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Errore salvataggio')
    }
  }

  if (isLoading) return <p className="text-xs text-[--km-text-muted]">Caricamento…</p>

  const years = [currentYear - 1, currentYear, currentYear + 1]

  return (
    <div className="flex flex-col gap-3">
      {years.map(year => {
        const t = targets?.find(t => t.year === year)
        const isEditing = editYear === year
        return (
          <div key={year} className="rounded-lg border border-white/5 bg-white/3 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[--km-text-primary]">{year}</span>
              {!isEditing && (
                <button
                  onClick={() => openEdit(year)}
                  className="text-xs text-indigo-400 hover:text-indigo-300"
                >
                  {t ? 'Modifica' : 'Imposta target'}
                </button>
              )}
            </div>
            {isEditing ? (
              <div className="flex flex-col gap-2">
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Revenue €', value: rev, setter: setRev },
                    { label: 'Margine €', value: mar, setter: setMar },
                    { label: 'Utilizz. %', value: util, setter: setUtil },
                  ].map(({ label, value, setter }) => (
                    <div key={label} className="flex flex-col gap-1">
                      <label className="text-xs text-[--km-text-muted]">{label}</label>
                      <input
                        type="number"
                        value={value}
                        onChange={e => setter(e.target.value)}
                        step="0.01"
                        min="0"
                        className="rounded border border-white/10 bg-white/5 px-2 py-1 text-xs text-[--km-text-primary] focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={upsert.isPending}
                    className="flex items-center gap-1 px-3 py-1 rounded bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white text-xs font-medium"
                  >
                    {upsert.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                    Salva
                  </button>
                  <button onClick={() => setEditYear(null)} className="text-xs text-[--km-text-muted]">
                    Annulla
                  </button>
                </div>
                {error && <p className="text-xs text-red-400">{error}</p>}
              </div>
            ) : t ? (
              <div className="grid grid-cols-3 gap-2 text-xs text-[--km-text-secondary]">
                <span>Rev: <span className="text-[--km-text-primary]">{t.target_revenue ? formatKpiEur(parseFloat(t.target_revenue)) : '—'}</span></span>
                <span>Mar: <span className="text-[--km-text-primary]">{t.target_margin ? formatKpiEur(parseFloat(t.target_margin)) : '—'}</span></span>
                <span>Util: <span className="text-[--km-text-primary]">{t.target_utilization ? `${t.target_utilization}%` : '—'}</span></span>
              </div>
            ) : (
              <p className="text-xs text-[--km-text-muted]">Nessun target impostato</p>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Riga BU con pannelli espandibili
// ---------------------------------------------------------------------------

function BuRow({
  bu,
  tenantServiceLines,
  onEdit,
  onDelete,
}: {
  bu: BusinessUnit
  tenantServiceLines: string[]
  onEdit: (bu: BusinessUnit) => void
  onDelete: (bu: BusinessUnit) => void
}) {
  const [openPanel, setOpenPanel] = useState<'mapping' | 'targets' | null>(null)

  return (
    <div className="rounded-xl border border-white/8 bg-white/3 overflow-hidden">
      <div className="flex items-center gap-4 px-4 py-3">
        <div
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: bu.color }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-[--km-text-primary] text-sm">{bu.name}</span>
            <span className="font-mono text-xs text-[--km-text-muted] bg-white/5 px-1.5 py-0.5 rounded">
              {bu.code}
            </span>
          </div>
          {bu.description && (
            <p className="text-xs text-[--km-text-muted] mt-0.5 truncate">{bu.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setOpenPanel(p => p === 'mapping' ? null : 'mapping')}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
              openPanel === 'mapping'
                ? 'bg-indigo-500/20 text-indigo-300'
                : 'text-[--km-text-muted] hover:text-[--km-text-secondary] hover:bg-white/5'
            }`}
            title="Mapping service line"
          >
            <Tag className="w-3.5 h-3.5" />
            Service line
            {openPanel === 'mapping' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          <button
            onClick={() => setOpenPanel(p => p === 'targets' ? null : 'targets')}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
              openPanel === 'targets'
                ? 'bg-indigo-500/20 text-indigo-300'
                : 'text-[--km-text-muted] hover:text-[--km-text-secondary] hover:bg-white/5'
            }`}
            title="Target KPI"
          >
            <Target className="w-3.5 h-3.5" />
            Target
            {openPanel === 'targets' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          <button
            onClick={() => onEdit(bu)}
            className="p-1.5 rounded-lg text-[--km-text-muted] hover:text-[--km-text-secondary] hover:bg-white/5 transition-colors"
            title="Modifica"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDelete(bu)}
            className="p-1.5 rounded-lg text-[--km-text-muted] hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="Elimina"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {openPanel === 'mapping' && (
        <div className="border-t border-white/5 px-4 py-3">
          <MappingPanel bu={bu} tenantServiceLines={tenantServiceLines} />
        </div>
      )}
      {openPanel === 'targets' && (
        <div className="border-t border-white/5 px-4 py-3">
          <TargetsPanel bu={bu} />
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pagina principale
// ---------------------------------------------------------------------------

export function BusinessUnitsPage() {
  const { data: tenant, isLoading: tenantLoading } = useTenant()
  const isEnterprise = tenant?.plan === 'enterprise'

  const { data: bus, isLoading, isError, error } = useBusinessUnits()
  const createBu = useCreateBusinessUnit()
  const updateBu = useUpdateBusinessUnit()
  const deleteBu = useDeleteBusinessUnit()

  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<BusinessUnit | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<BusinessUnit | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const tenantServiceLines: string[] =
    (tenant?.settings as { service_lines?: string[] } | undefined)?.service_lines ?? ['SG', 'LIC', 'DEV', 'RIV', 'PS']

  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[--km-text-muted]" />
      </div>
    )
  }

  if (!isEnterprise) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <PageHeader title="Business Unit" subtitle="Segmenta i KPI per divisione aziendale" />
        <EnterprisePaywall />
      </div>
    )
  }

  const handleCreate = async (data: { name: string; code: string; description: string; color: string }) => {
    setFormError(null)
    try {
      await createBu.mutateAsync(data)
      setShowForm(false)
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Errore creazione')
    }
  }

  const handleUpdate = async (data: { name: string; code: string; description: string; color: string }) => {
    if (!editing) return
    setFormError(null)
    try {
      await updateBu.mutateAsync({ id: editing.id, payload: data })
      setEditing(null)
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Errore aggiornamento')
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteBu.mutateAsync(deleteTarget.id)
      setDeleteTarget(null)
    } catch {
      // errore già gestito nel mutation
    }
  }

  if (isError && error instanceof PlanRequiredError) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <PageHeader title="Business Unit" subtitle="Segmenta i KPI per divisione aziendale" />
        <EnterprisePaywall />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 flex flex-col gap-6">
      <div className="flex items-start justify-between">
        <PageHeader title="Business Unit" subtitle="Segmenta ricavi, margini e ore per divisione aziendale" />
        {!showForm && !editing && (
          <button
            onClick={() => { setShowForm(true); setFormError(null) }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nuova BU
          </button>
        )}
      </div>

      {isError && (
        <AlertBanner severity="critical" message="Errore caricamento Business Unit. Riprova." />
      )}

      {/* Form creazione */}
      {showForm && (
        <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-5">
          <h3 className="text-sm font-semibold text-[--km-text-primary] mb-4">Nuova Business Unit</h3>
          {formError && <AlertBanner severity="critical" message={formError} />}
          <BuForm
            onSave={handleCreate}
            onCancel={() => { setShowForm(false); setFormError(null) }}
            isLoading={createBu.isPending}
          />
        </div>
      )}

      {/* Form modifica */}
      {editing && (
        <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-5">
          <h3 className="text-sm font-semibold text-[--km-text-primary] mb-4">Modifica {editing.name}</h3>
          {formError && <AlertBanner severity="critical" message={formError} />}
          <BuForm
            initial={editing}
            onSave={handleUpdate}
            onCancel={() => { setEditing(null); setFormError(null) }}
            isLoading={updateBu.isPending}
          />
        </div>
      )}

      {/* Lista BU */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-[--km-text-muted] text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
        </div>
      ) : !bus || bus.length === 0 ? (
        <div className="text-center py-12 text-[--km-text-muted]">
          <p className="text-sm">Nessuna Business Unit configurata.</p>
          <p className="text-xs mt-1">Crea la prima BU per iniziare a segmentare i tuoi KPI.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {bus.map(bu => (
            <BuRow
              key={bu.id}
              bu={bu}
              tenantServiceLines={tenantServiceLines}
              onEdit={b => { setEditing(b); setShowForm(false); setFormError(null) }}
              onDelete={b => setDeleteTarget(b)}
            />
          ))}
        </div>
      )}

      {/* Dialog conferma eliminazione */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="rounded-2xl border border-white/10 bg-[--km-surface] p-6 w-full max-w-sm mx-4 shadow-2xl">
            <h3 className="font-semibold text-[--km-text-primary] mb-2">Elimina Business Unit</h3>
            <p className="text-sm text-[--km-text-secondary] mb-6">
              Vuoi eliminare <strong>{deleteTarget.name}</strong>? I dati storici classificati con questa BU non verranno eliminati.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                disabled={deleteBu.isPending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-400 disabled:opacity-50 text-white text-sm font-medium transition-colors"
              >
                {deleteBu.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Elimina
              </button>
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 px-4 py-2 rounded-lg border border-white/10 text-[--km-text-secondary] text-sm hover:bg-white/5 transition-colors"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
